import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const allocationCode = `// Architectural Logic: Allocation Realities
// 1. Stack Allocation (Static): Primitive values and execution context
const id = 101; 
const status = 'active';

// 2. Heap Allocation (Dynamic): Objects, Arrays, and Functions
const userProfile = {
    metadata: new Array(5000).fill('cached_node'),
    timestamp: Date.now()
};

// 3. Functional Allocation: Releasing memory when the scope closes
function processBuffer() {
    const buffer = Buffer.alloc(1024); // Large memory request
    return buffer.toString();
} // Fixed: buffer is eligible for GC after this point`;

const leakCode = `// Performance Risks: Common Memory Leaks
// 1. Forgotten Intervals: The "Zombie" Loop
const startTracking = () => {
    const data = fetchHeavyDataset();
    setInterval(() => {
        // This interval keeps 'data' in memory forever
        console.log("Tracking iteration:", data.id);
    }, 1000);
};

// 2. Detached DOM Nodes: Ghost Elements
let detachedElement;
function removeNode() {
    const btn = document.getElementById('submit');
    detachedElement = btn; // Reference kept despite removal
    document.body.removeChild(btn);
} // The button remains in memory because 'detachedElement' persists.`;

const weakMapCode = `// Defensive Engineering: WeakMap for Auto-Cleanup
// Unlike Map, WeakMap does not prevent garbage collection of its keys.
const securityMetadata = new WeakMap();

let userNode = { id: 405, type: 'authorized' };

// Associate data with the object
securityMetadata.set(userNode, { lastLogin: '2024-03-21' });

// Nullify the reference
userNode = null; 

// Result: The metadata is automatically eligible for GC because 
// the WeakMap link does not count as a hard reference.`;

const JSMemory = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Memory Engineering</div>
                <h1>JS Memory Management: Under the V8 Hood</h1>
                <p className="hero-lead">
                    Master the mechanics of memory allocation, the V8 garbage collection cycle,
                    and how to engineer leak-proof JavaScript applications that maintain high
                    performance over long runtimes.
                </p>
            </div>

            <div className="content-section">
                <h2>The V8 Memory Model: Heap vs. Stack</h2>
                <p>
                    JavaScript engines like V8 use two distinct memory structures. The **Stack** is used for
                    static data (primitives, pointers) and manages execution flow via a "Last-In-First-Out"
                    logic. The **Heap** is a larger, disorganized region used for dynamic data like
                    objects and arrays.
                </p>
                <CodeBlock code={allocationCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Garbage Collection: The Mark-and-Sweep Algorithm</h2>
                <p>
                    Modern engines use a **Mark-and-Sweep** strategy. The GC starts from "roots" (like the
                    global `window` object) and "marks" every object it can reach. Any object that remains
                    unmarked is considered unreachable and is "swept" away to reclaim memory.
                </p>
                <ProTip><p>
                        V8 separates the heap into **Young Generation** (newly created objects) and
                        **Old Generation** (objects that survived multiple GC cycles). The Young
                        Generation is cleaned very frequently and quickly, while the Old Generation
                        requires more intensive compute to clear.
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>Architecting Against Leaks</h2>
                <p>
                    A memory leak isn't a bug that crashes your app immediately; it's a "slow death" where
                    memory usage climbs until the system runs out of resources. The most frequent
                    culprits are **unbounded observers**, **global variables**, and **detached DOM nodes**.
                </p>
                <CodeBlock code={leakCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The WeakMap Solution</h2>
                <p>
                    One of the most effective tools for preventing leaks in complex applications is the
                    `WeakMap`. It allows you to associate data with an object without creating a
                    "strong" reference that blocks the garbage collector.
                </p>
                <CodeBlock code={weakMapCode} language="javascript" />
            </div>

            <KeyFacts><ul>
                    <li>**Lifecycle:** Always nullify references to heavy objects once processing is complete.</li>
                    <li>**Timers:** Use `clearInterval` or `clearTimeout` in component teardown phases.</li>
                    <li>**Observability:** Use Chrome DevTools "Memory" tab to take Heap Snapshots.</li>
                    <li>**Data Structures:** Prefer `WeakMap` or `WeakSet` for object-metadata mapping.</li>
                    <li>**Pagination:** Never load entire datasets into memory; use paginated streaming.</li>
                    <li>**Virtualization:** For long lists, use virtual scrolling to limit the number of active DOM nodes.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSMemory;


